import React from "react";
import classNames from "classnames";
import { statusBG, statusColor } from "../../utils/leadUtils";
import Dropdown from 'react-bootstrap/Dropdown';

import ChevronIcon from "../../assets/icons/ChevronIcon";
import './LeadDropdown.scss';

// multiple: allows selecting 
/**
 * Lead Status Dropdown Selector
 * 
 * @component
 * @param {boolean} props.multiple - Allow selection of multiple statuses
 * @param {'discreet', 'large'} type - The type of component used
 * @returns {JSX.Element}
 */
export default function LeadStatusDropdown({ lead, statuses, selectedId, selectedIds, onChange, type, showCreate, onCreate, placeholder, fixed, disabled, multiple }) {
  
  if (!statuses) {
    return <div>...</div>;
  }

  const selectedStatus = statuses?.find(status => status.id === selectedId || status.id === lead?.status_id);

  const onAddStatus = id => {
    if (multiple) {
      onChange([...selectedIds, id]);
    }
    else {
      onChange(id);
    }
  }

  const onRemoveStatus = (e, id) => {
    e.stopPropagation();

    const ids = [...selectedIds];
    const index = ids.findIndex(value => value === id);
    ids.splice(index, 1);
    onChange(ids);
  }

  const renderPill = id => {
    const status = statuses.find(status => status.id === id);

    const style = {
      color           : statusColor(status),
      borderColor     : statusColor(status),
      backgroundColor : statusBG(status),
    }

    // the first button prevents the event bubbling to the dropdown (stops propagation)
    // the second div is role button (since a real <button> cannot be nested):
    return (
      <button key={id} type='button' className="pill" onClick={e => e.stopPropagation()} style={style}>
        <div className="pill-label">{ status.name }</div>
        <div className="pill-remove" role="button" onClick={e => onRemoveStatus(e, id)}>×</div>
      </button>
    );
  }

  const renderToggle = () => {
    if (multiple) {
      if (selectedIds.length > 0) {
        return (
          <div className='multiple-labels'>{ selectedIds.map(id => renderPill(id)) }</div>
        );
      }
      else {
        return <div className='label unassigned'>{ placeholder || 'No status set' }</div>
      }
    }
    else {
      return <>
        { selectedStatus && <div className='square' style={{ backgroundColor: `#${selectedStatus.color}` }} /> }
        <div className={classNames('label', { unassigned: !selectedStatus })}>{ selectedStatus?.name || placeholder || 'No status set' }</div>
      </>
    };
  }

  const visibleStatuses = multiple ? statuses.filter(status => !selectedIds.includes(status.id)) : statuses;

  return (
    <Dropdown className={classNames('__lead-dropdown', { [`__lead-dropdown-${type}`]: type })}>
      <Dropdown.Toggle disabled={disabled} as={Toggle} className='lead-toggle' type="button">
        <div className="toggle-overflow">
          { renderToggle() }
          <ChevronIcon className='chevron' />
        </div>
      </Dropdown.Toggle>
      <Dropdown.Menu renderOnMount={fixed} popperConfig={fixed && { strategy: 'fixed' }}>
        { statuses.length === 0 && (
          <Dropdown.ItemText className="empty-text">(No statuses found)</Dropdown.ItemText>
        )}
        { multiple && statuses.length === selectedIds.length && (
          <Dropdown.ItemText className="empty-text">No more statuses to select</Dropdown.ItemText>
        )}

        { visibleStatuses.map(status => (
          <Dropdown.Item key={status.id} as='button' type="button" active={status.id === selectedStatus?.id} onClick={() => onAddStatus(status.id)}>
            <div className='square' style={{ backgroundColor: `#${status.color}` }} />
            <div className='status-label'>{ status.name }</div>
          </Dropdown.Item>
        ))}
        { showCreate && (
          <>
            <Dropdown.Divider />
            <Dropdown.Item as='button' onClick={() => onCreate()} className="create-new">＋ Create New Status</Dropdown.Item>
          </>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
}

const Toggle = React.forwardRef(({ children, onClick, ...props }, ref) => (
  <button onClick={onClick} ref={ref} {...props}>
    { children }
  </button>
));
